/**
 * DJBookingFlow - Instant booking for DJs & Bands with packages
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Calendar as CalendarIcon, Clock, Music, Check, ChevronRight, Loader2,
  MapPin, Headphones, Mic2, Speaker
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_OPTIONS = [
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'
];

const MUSIC_GENRES = [
  'EDM', 'Pop', 'Hip-Hop', 'R&B', 'Rock', 'Jazz', 'Baila', 'Bollywood', 
  'Latin', '80s/90s', 'House', 'Techno', 'Afrobeats', 'Reggaeton'
];

const EVENT_TYPES = [
  { value: 'weddings', label: 'Wedding' },
  { value: 'birthday', label: 'Birthday Party' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'kids', label: 'Kids Party' },
  { value: 'other', label: 'Other' }
];

export default function DJBookingFlow({ open, onClose, provider, onBookingComplete }) {
  const [step, setStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [eventType, setEventType] = useState('');
  const [location, setLocation] = useState('');
  const [setupTime, setSetupTime] = useState('60');
  const [musicPreferences, setMusicPreferences] = useState([]);
  const [songRequests, setSongRequests] = useState('');
  const [doNotPlay, setDoNotPlay] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const packages = provider?.packages || [];
  const addOns = provider?.add_ons || [];
  const djDetails = provider?.dj_details || provider?.band_details || {};
  const currency = provider?.currency || 'Rs';

  const calculateTotal = () => {
    let total = selectedPackage?.price || 0;
    selectedAddOns.forEach(aoId => {
      const addOn = addOns.find(a => a.id === aoId);
      if (addOn) total += addOn.price;
    });
    return total;
  };

  const calculateEndTime = () => {
    if (!startTime || !selectedPackage) return '';
    const hours = parseInt(selectedPackage.duration);
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = startHour + hours;
    return `${endHour.toString().padStart(2, '0')}:00`;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingData = {
        provider_id: provider.id,
        event_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: calculateEndTime(),
        package_id: selectedPackage.id,
        event_type: eventType,
        location: location,
        add_ons: selectedAddOns,
        music_preferences: musicPreferences,
        song_requests: songRequests + (doNotPlay ? `\n\nDo NOT play: ${doNotPlay}` : ''),
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/bookings/dj`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        toast.success('DJ booking confirmed! Check your email for details.');
        onBookingComplete?.();
        onClose();
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      toast.error('Failed to complete booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Select Package</h3>
            
            <div className="space-y-3">
              {packages.map(pkg => (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    selectedPackage?.id === pkg.id
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 hover:border-cyan-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-lg">{pkg.name}</p>
                      <p className="text-sm text-gray-600">{pkg.duration}</p>
                      {pkg.includes && (
                        <ul className="mt-2 text-sm text-gray-500">
                          {pkg.includes.map((item, i) => (
                            <li key={i} className="flex items-center gap-1">
                              <Check className="w-3 h-3 text-green-500" /> {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-xl text-cyan-600">{currency} {pkg.price?.toLocaleString()}</p>
                      {selectedPackage?.id === pkg.id && (
                        <Check className="w-6 h-6 text-cyan-600 ml-auto mt-2" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Add-ons */}
            {addOns.length > 0 && (
              <div className="pt-4 border-t">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Add-ons</label>
                <div className="grid grid-cols-2 gap-2">
                  {addOns.map(addOn => (
                    <label key={addOn.id} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={selectedAddOns.includes(addOn.id)}
                        onCheckedChange={(checked) => {
                          setSelectedAddOns(prev => 
                            checked ? [...prev, addOn.id] : prev.filter(id => id !== addOn.id)
                          );
                        }}
                      />
                      <span className="text-sm">{addOn.name}</span>
                      <span className="ml-auto text-sm font-bold text-cyan-600">+{currency} {addOn.price?.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Equipment included */}
            {djDetails.equipment_included && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800 flex items-center gap-2">
                  <Speaker className="w-4 h-4" />
                  Sound equipment included in all packages
                </p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Event Details</h3>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Date</label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date > addDays(new Date(), 180)}
                  className="rounded-md border"
                />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Time</label>
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger>
                      <Clock className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Event Type</label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Venue Address/Postcode</label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Where is the event?"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Setup Time Needed</label>
                  <Select value={setupTime} onValueChange={setSetupTime}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes before</SelectItem>
                      <SelectItem value="60">1 hour before</SelectItem>
                      <SelectItem value="90">1.5 hours before</SelectItem>
                      <SelectItem value="120">2 hours before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Music Preferences</h3>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Select Genres (choose multiple)</label>
              <div className="flex flex-wrap gap-2">
                {(djDetails.genres || MUSIC_GENRES).map(genre => (
                  <button
                    key={genre}
                    onClick={() => {
                      setMusicPreferences(prev => 
                        prev.includes(genre) 
                          ? prev.filter(g => g !== genre)
                          : [...prev, genre]
                      );
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      musicPreferences.includes(genre)
                        ? 'bg-cyan-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Song Requests (Optional)</label>
              <Textarea
                value={songRequests}
                onChange={(e) => setSongRequests(e.target.value)}
                placeholder="Any specific songs you'd like played..."
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Do NOT Play List (Optional)</label>
              <Textarea
                value={doNotPlay}
                onChange={(e) => setDoNotPlay(e.target.value)}
                placeholder="Songs or genres to avoid..."
                rows={2}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Contact & Confirm</h3>
            
            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Package</span>
                <span className="font-medium">{selectedPackage?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date</span>
                <span>{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time</span>
                <span>{startTime} - {calculateEndTime()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Location</span>
                <span>{location || 'TBC'}</span>
              </div>
              {selectedAddOns.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Add-ons</span>
                  <span>{selectedAddOns.length} selected</span>
                </div>
              )}
              {musicPreferences.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Genres</span>
                  <span className="text-right max-w-[150px] truncate">{musicPreferences.join(', ')}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                <span>Total</span>
                <span className="text-cyan-600">{currency} {calculateTotal().toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-cyan-700">
                <span>Deposit (25%)</span>
                <span>{currency} {(calculateTotal() * 0.25).toLocaleString()}</span>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5 text-cyan-600" />
            Book {provider.provider_type === 'BAND' ? 'Band' : 'DJ'}
          </DialogTitle>
          <DialogDescription>{provider.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-cyan-600 text-white' :
                s === step ? 'bg-cyan-100 text-cyan-700 border-2 border-cyan-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-1 ${s < step ? 'bg-cyan-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}
          
          {step < 4 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !selectedPackage) ||
                (step === 2 && (!selectedDate || !startTime || !eventType || !location))
              }
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Confirm Booking
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
